/**
 * The "scheduled" strip at the top of the Candidate Requests tab.
 *
 * Sits above the reminders strip and mirrors it: the recruiter's own queued replies, soonest first,
 * with the cancel button — because a queued reply is the one thing on this tab that will act without
 * anyone touching it again, so it needs a single obvious place to stop it.
 *
 * Also where a FAILURE shows up. There is no notification channel on this tab today, so a row that
 * ended `failed` or `superseded` stays here in red until it is dismissed. That is deliberate: a
 * reply that silently never went out is the worst outcome this feature can have.
 *
 * Renders nothing when there is nothing to show, and nothing when the load failed — the tab must not
 * degrade over an aid. (A failed load also means the card shows its ordinary Send button again; the
 * server still refuses a second answer to the same question, so the cost is a confusing button and
 * not two mails.)
 */
import { useEffect, useState } from 'react';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { X } from 'lucide-react';
import { COLORS } from './format';
import { RescheduleSendModal } from './RescheduleSendModal';
import { describeFailure, describeHold, formatSendAt } from './scheduleReplyTime';
import { useScheduledSends } from './useScheduledSends';
import type { ScheduledSend } from '../../services/taScheduledSendsApi';

const gmailUrl = (threadId: string) =>
  `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(threadId)}`;

/** Rows worth a line: still to go, or ended in a way the recruiter has to know about. */
const isVisible = (r: ScheduledSend) =>
  r.status === 'pending' || r.status === 'sending' || r.status === 'superseded' || r.status === 'failed';

function ScheduledRow({
  row,
  now,
  noun,
  onOpen,
  onEdit,
  onCancel,
  onDismiss,
}: {
  row: ScheduledSend;
  /** Sampled once per strip render — render functions must stay pure (react-compiler lint). */
  now: number;
  /** "Reply" or "Rejection" — a rejection row is a fresh email, not a thread reply. */
  noun: string;
  onOpen?: () => void;
  onEdit: () => void;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const bad = row.status === 'failed' || row.status === 'superseded';
  /**
   * Due, but the worker is deliberately not sending it (the pilot lock). Amber, not red: nothing has
   * failed and the recruiter can still cancel or re-time it — but "queued" would be the wrong word
   * for a reply whose time has passed, and saying nothing is how one went unnoticed all morning.
   */
  const held = row.status === 'pending' && Boolean(row.heldCode);
  const name = row.candidateName || 'this candidate';
  return (
    <li className="flex flex-wrap items-center gap-2 py-1.5">
      <span
        className="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold"
        style={
          bad
            ? { backgroundColor: COLORS.redBg, color: COLORS.redText }
            : held
              ? { backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }
              : { backgroundColor: '#EEF4FC', color: '#1D4ED8' }
        }
      >
        {bad ? 'not sent' : held ? 'on hold' : formatSendAt(row.sendAt, new Date(now))}
      </span>
      <span className="min-w-0 flex-1 text-sm text-gray-800">
        {bad ? (
          <>
            {noun} to <strong>{name}</strong> — {row.terminalNote || describeFailure(row.lastErrorCode)}
          </>
        ) : held ? (
          <>
            {noun} to <strong>{name}</strong> {describeHold(row.heldCode ?? null)}
          </>
        ) : (
          <>
            {noun} to <strong>{name}</strong>
            {row.status === 'sending' ? ' is going out now' : ' is queued'}
          </>
        )}
      </span>
      {onOpen && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onOpen}
          className="shrink-0"
          title="Open the request this reply belongs to, here in the tab"
        >
          ✉ Open request
        </Button>
      )}
      {row.threadId && (
        <a
          href={gmailUrl(row.threadId)}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 text-xs text-gray-400 hover:underline"
          title="The same thread in Gmail"
        >
          Gmail ↗
        </a>
      )}
      {bad ? (
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Dismiss"
          onClick={onDismiss}
          className="shrink-0"
          title="Clear this from the strip"
        >
          <X className="h-4 w-4" />
        </Button>
      ) : (
        <>
          {/* Time only — the words are not editable here; see RescheduleSendModal. */}
          <Button
            size="sm"
            variant="ghost"
            onClick={onEdit}
            disabled={row.status === 'sending'}
            className="shrink-0"
            title={
              row.status === 'sending'
                ? 'Going out right now — the time can no longer be changed'
                : 'Change when this reply goes out'
            }
          >
            ✎ Edit time
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={row.status === 'sending'}
            className="shrink-0"
            title={
              row.status === 'sending'
                ? 'Going out right now — too late to stop it'
                : 'Call this reply back; nothing will be sent'
            }
          >
            Cancel
          </Button>
        </>
      )}
    </li>
  );
}

export function ScheduledSendsStrip({
  kind = 'reply',
  onOpenThread,
}: {
  /**
   * Which queued sends this strip owns: 'reply' on the Requests tab, 'rejection' on Feedback. The
   * store holds both (one endpoint), so each surface filters to its own — otherwise a queued
   * rejection would read as a "Reply" with a dead Gmail link on the Requests tab.
   */
  kind?: 'reply' | 'rejection';
  /**
   * Focus the request IN THE TOOL. Returns false when the card is not in the queue — see below for
   * why that has to be said out loud rather than silently searched for.
   */
  onOpenThread?: (threadId: string, candidateName: string | null) => boolean;
}) {
  const { scheduled, failed, cancel, dismiss } = useScheduledSends();
  const noun = kind === 'rejection' ? 'Rejection' : 'Reply';
  const [editing, setEditing] = useState<ScheduledSend | null>(null);
  /**
   * A ticking minute clock in state, like the reminders strip: a bare `Date.now()` per row render is
   * impure (react-compiler lint), and without the tick a row queued for 14:00 would still read
   * "~14:00" long after it had gone out on a tab left open.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, []);
  const rows = scheduled.filter(isVisible).filter((r) => (r.kind || 'reply') === kind);
  if (failed || rows.length === 0) return null;

  return (
    /* Same shell, heading and muted-note styling as RemindersStrip — the two sit on top of each
       other, so anything different reads as a different KIND of thing rather than a sibling. */
    <div className="rounded-lg border border-gray-200 bg-white p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-800">
          {kind === 'rejection' ? 'Scheduled rejections' : 'Scheduled replies'}{' '}
          <span className="font-normal text-gray-400">— only you see these</span>
        </p>
      </div>
      <ul className="divide-y divide-gray-100">
        {rows.map((row) => (
          <ScheduledRow
            key={row.id}
            row={row}
            now={now}
            noun={noun}
            onOpen={
              onOpenThread
                ? () => {
                    /**
                     * A card removed from the overview is recorded as handled and does not come
                     * back to the queue, so the name-search fallback finds nothing and the click
                     * reads as broken. Say what is true instead: the reply is still going out, and
                     * the thread is one click away in Gmail.
                     */
                    if (!row.threadId || !onOpenThread(row.threadId, row.candidateName)) {
                      showToast({
                        status: 'info',
                        description:
                          'That request is no longer in the overview. The reply is still scheduled — open the thread with Gmail ↗, or cancel it here.',
                      });
                    }
                  }
                : undefined
            }
            onEdit={() => setEditing(row)}
            onCancel={() => void cancel(row)}
            onDismiss={() => {
              void logAnalyticsEvent('ta_reply_schedule_dismissed', { status: row.status });
              dismiss(row);
            }}
          />
        ))}
      </ul>
      {editing && (
        <RescheduleSendModal
          // Keyed by row: the modal seeds its field on mount, so switching straight from editing one
          // reply to another must remount it or the first one's time sticks (the reminders lesson).
          key={editing.id}
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
      <p className="mt-1 text-xs text-gray-400">
        Sent from your own mailbox at about the time shown. Not in Gmail&apos;s
        &quot;Scheduled&quot; folder — cancel here.
      </p>
    </div>
  );
}
